import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from "@/lib/utils";
import { WidgetProps } from '@rjsf/utils';
import {
  Accessibility,
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  Award,
  Baby,
  BadgeCheck,
  BadgePercent,
  Ban,
  Banknote,
  BarChart,
  BarChart2,
  Bell,
  BellRing,
  Bluetooth,
  Bookmark,
  BookOpen,
  Box,
  Boxes,
  CalendarCheck,
  CalendarClock,
  Camera,
  // Validation & Success
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleSlash,
  Clipboard,
  ClipboardCheck,
  // Time & Speed
  Clock,
  Clock2,
  Cloud,
  CloudDownload,
  CloudSun,
  CloudUpload,
  Cog,
  Copy,
  Cpu,
  CreditCard,
  Crown,
  Diamond,
  // Money & Commerce
  DollarSign,
  Download,
  Droplets,
  Euro,
  ExternalLink,
  Eye,
  EyeOff,
  // Files & Documents
  File,
  FileCheck,
  FileText,
  Filter,
  Fingerprint,
  Flag,
  Flame,
  Flower2,
  FolderCheck,
  Gauge,
  Gem,
  Gift,
  // Global & Communication
  Globe,
  Globe2,
  GraduationCap,
  Grid,
  Hammer,
  Handshake,
  Headphones,
  // Appreciation & Emotions
  Heart,
  HeartHandshake,
  HelpCircle,
  // Navigation & UI
  Home,
  Hourglass,
  // Media & Content
  Image,
  Images,
  // Misc
  Info,
  KeyRound,
  Languages,
  Laptop,
  Layers,
  // Nature & Sustainability
  Leaf,
  Lightbulb,
  LineChart,
  Link,
  List,
  Lock,
  LockKeyhole,
  Mail,
  MapPin,
  Medal,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquare,
  Mic,
  Minus,
  Monitor,
  Moon,
  MoreHorizontal,
  MoreVertical,
  Music,
  Navigation,
  Package,
  PackageCheck,
  PackageOpen,
  PartyPopper,
  Percent,
  Phone,
  PhoneCall,
  PieChart,
  PiggyBank,
  Pin,
  Plane,
  Play,
  PlayCircle,
  Plus,
  Printer,
  Puzzle,
  QrCode,
  Receipt,
  Recycle,
  RefreshCw,
  Rocket,
  RotateCcw,
  Scan,
  Search,
  Send,
  Server,
  // Tools & Settings
  Settings,
  Share,
  Share2,
  // Security & Trust
  Shield,
  ShieldAlert,
  ShieldCheck,
  Ship,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Sprout,
  Star,
  Stars,
  Store,
  Sun,
  Tag,
  Tags,
  // Goals & Growth
  Target,
  ThumbsUp,
  Timer,
  TimerOff,
  TreeDeciduous,
  TrendingDown,
  TrendingUp,
  Trophy,
  // Shipping & Delivery
  Truck,
  Upload,
  // Users & People
  User,
  UserCheck,
  UserPlus,
  Users,
  UsersRound,
  Video,
  Wallet,
  // Technology
  Wifi,
  WifiOff,
  Wrench,
  X,
  Zap,
  ZapOff,
  type LucideIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Map of icon names to their components
const ICON_MAP: Record<string, LucideIcon> = {
  // Validation & Success
  Check,
  CheckCircle,
  CheckCircle2,
  BadgeCheck,
  CircleCheck,
  ThumbsUp,
  // Security & Trust
  Shield,
  ShieldCheck,
  ShieldAlert,
  Lock,
  LockKeyhole,
  KeyRound,
  Fingerprint,
  Eye,
  EyeOff,
  // Time & Speed
  Clock,
  Clock2,
  Timer,
  TimerOff,
  Hourglass,
  CalendarCheck,
  CalendarClock,
  Zap,
  ZapOff,
  Rocket,
  Gauge,
  // Appreciation & Emotions
  Heart,
  HeartHandshake,
  Star,
  Stars,
  Award,
  Trophy,
  Medal,
  Crown,
  Gift,
  PartyPopper,
  Sparkles,
  Gem,
  Diamond,
  Flame,
  // Shipping & Delivery
  Truck,
  Package,
  PackageCheck,
  PackageOpen,
  Box,
  Boxes,
  MapPin,
  Navigation,
  Plane,
  Ship,
  // Nature & Sustainability
  Leaf,
  TreeDeciduous,
  Flower2,
  Sprout,
  Sun,
  Moon,
  CloudSun,
  Droplets,
  Recycle,
  // Global & Communication
  Globe,
  Globe2,
  Languages,
  MessageCircle,
  MessageSquare,
  Mail,
  Phone,
  PhoneCall,
  Send,
  Bell,
  BellRing,
  Megaphone,
  // Goals & Growth
  Target,
  TrendingUp,
  TrendingDown,
  BarChart,
  BarChart2,
  PieChart,
  LineChart,
  Activity,
  ArrowUpRight,
  ArrowUp,
  // Money & Commerce
  DollarSign,
  Euro,
  CreditCard,
  Wallet,
  Banknote,
  PiggyBank,
  Receipt,
  ShoppingCart,
  ShoppingBag,
  Store,
  Tag,
  Tags,
  Percent,
  BadgePercent,
  // Users & People
  User,
  Users,
  UserCheck,
  UserPlus,
  UsersRound,
  Handshake,
  Baby,
  Accessibility,
  // Tools & Settings
  Settings,
  Wrench,
  Hammer,
  Cog,
  SlidersHorizontal,
  Filter,
  // Media & Content
  Image,
  Images,
  Camera,
  Video,
  Play,
  PlayCircle,
  Music,
  Headphones,
  Mic,
  // Files & Documents
  File,
  FileText,
  FileCheck,
  FolderCheck,
  ClipboardCheck,
  BookOpen,
  GraduationCap,
  // Technology
  Wifi,
  WifiOff,
  Bluetooth,
  Monitor,
  Smartphone,
  Laptop,
  Cpu,
  Server,
  Cloud,
  CloudDownload,
  CloudUpload,
  Download,
  Upload,
  RefreshCw,
  RotateCcw,
  // Navigation & UI
  Home,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  Link,
  Menu,
  X,
  Plus,
  Minus,
  MoreHorizontal,
  MoreVertical,
  Grid,
  List,
  // Misc
  Info,
  HelpCircle,
  AlertCircle,
  AlertTriangle,
  Ban,
  CircleSlash,
  Lightbulb,
  Puzzle,
  Layers,
  Copy,
  Clipboard,
  QrCode,
  Scan,
  Printer,
  Share,
  Share2,
  Bookmark,
  Flag,
  Pin,
};

// Icon categories for better organization
const ICON_CATEGORIES: Record<string, string[]> = {
  '✓ Validation': ['Check', 'CheckCircle', 'CheckCircle2', 'BadgeCheck', 'CircleCheck', 'ThumbsUp'],
  '🔒 Security': [
    'Shield',
    'ShieldCheck',
    'ShieldAlert',
    'Lock',
    'LockKeyhole',
    'KeyRound',
    'Fingerprint',
    'Eye',
    'EyeOff',
  ],
  '⚡ Time & Speed': [
    'Clock',
    'Clock2',
    'Timer',
    'TimerOff',
    'Hourglass',
    'CalendarCheck',
    'CalendarClock',
    'Zap',
    'ZapOff',
    'Rocket',
    'Gauge',
  ],
  '❤️ Appreciation': [
    'Heart',
    'HeartHandshake',
    'Star',
    'Stars',
    'Award',
    'Trophy',
    'Medal',
    'Crown',
    'Gift',
    'PartyPopper',
    'Sparkles',
    'Gem',
    'Diamond',
    'Flame',
  ],
  '📦 Shipping': [
    'Truck',
    'Package',
    'PackageCheck',
    'PackageOpen',
    'Box',
    'Boxes',
    'MapPin',
    'Navigation',
    'Plane',
    'Ship',
  ],
  '🌿 Nature': [
    'Leaf',
    'TreeDeciduous',
    'Flower2',
    'Sprout',
    'Sun',
    'Moon',
    'CloudSun',
    'Droplets',
    'Recycle',
  ],
  '🌍 Communication': [
    'Globe',
    'Globe2',
    'Languages',
    'MessageCircle',
    'MessageSquare',
    'Mail',
    'Phone',
    'PhoneCall',
    'Send',
    'Bell',
    'BellRing',
    'Megaphone',
  ],
  '📈 Growth': [
    'Target',
    'TrendingUp',
    'TrendingDown',
    'BarChart',
    'BarChart2',
    'PieChart',
    'LineChart',
    'Activity',
    'ArrowUpRight',
    'ArrowUp',
  ],
  '💰 Commerce': [
    'DollarSign',
    'Euro',
    'CreditCard',
    'Wallet',
    'Banknote',
    'PiggyBank',
    'Receipt',
    'ShoppingCart',
    'ShoppingBag',
    'Store',
    'Tag',
    'Tags',
    'Percent',
    'BadgePercent',
  ],
  '👥 People': ['User', 'Users', 'UserCheck', 'UserPlus', 'UsersRound', 'Handshake', 'Baby', 'Accessibility'],
  '🔧 Tools': ['Settings', 'Wrench', 'Hammer', 'Cog', 'SlidersHorizontal', 'Filter'],
  '🎬 Media': ['Image', 'Images', 'Camera', 'Video', 'Play', 'PlayCircle', 'Music', 'Headphones', 'Mic'],
  '📄 Documents': [
    'File',
    'FileText',
    'FileCheck',
    'FolderCheck',
    'ClipboardCheck',
    'BookOpen',
    'GraduationCap',
  ],
  '💻 Technology': [
    'Wifi',
    'WifiOff',
    'Bluetooth',
    'Monitor',
    'Smartphone',
    'Laptop',
    'Cpu',
    'Server',
    'Cloud',
    'CloudDownload',
    'CloudUpload',
    'Download',
    'Upload',
    'RefreshCw',
    'RotateCcw',
  ],
  '🧭 Navigation': [
    'Home',
    'Search',
    'ChevronDown',
    'ChevronRight',
    'ChevronLeft',
    'ChevronUp',
    'ArrowRight',
    'ArrowLeft',
    'ExternalLink',
    'Link',
    'Menu',
    'X',
    'Plus',
    'Minus',
    'MoreHorizontal',
    'MoreVertical',
    'Grid',
    'List',
  ],
  '💡 Misc': [
    'Info',
    'HelpCircle',
    'AlertCircle',
    'AlertTriangle',
    'Ban',
    'CircleSlash',
    'Lightbulb',
    'Puzzle',
    'Layers',
    'Copy',
    'Clipboard',
    'QrCode',
    'Scan',
    'Printer',
    'Share',
    'Share2',
    'Bookmark',
    'Flag',
    'Pin',
  ],
};

/**
 * IconSelector widget for selecting icons with visual preview and search
 */
function IconSelectorWidget(props: WidgetProps) {
  const { value, onChange, id, disabled, readonly, schema, ref } = props;
  const { t } = useTranslation('storefront');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get available icons from schema enum or use all icons
  const availableIcons = useMemo(() => {
    if (schema.enum && Array.isArray(schema.enum)) {
      return schema.enum as string[];
    }
    return Object.keys(ICON_MAP);
  }, [schema.enum]);

  // Filter icons based on search query
  const filteredIcons = useMemo(() => {
    if (!searchQuery) return availableIcons;
    const query = searchQuery.toLowerCase();
    return availableIcons.filter((iconName) => iconName.toLowerCase().includes(query));
  }, [availableIcons, searchQuery]);

  // Group filtered icons by category
  const groupedIcons = useMemo(() => {
    const groups: Record<string, string[]> = {};

    // First, add icons to their categories
    for (const [category, icons] of Object.entries(ICON_CATEGORIES)) {
      const filtered = icons.filter((icon) => filteredIcons.includes(icon));
      if (filtered.length > 0) {
        groups[category] = filtered;
      }
    }

    // Add any remaining icons that aren't in a category
    const categorizedIcons = Object.values(ICON_CATEGORIES).flat();
    const uncategorized = filteredIcons.filter((icon) => !categorizedIcons.includes(icon));
    if (uncategorized.length > 0) {
      groups['Other'] = uncategorized;
    }

    return groups;
  }, [filteredIcons]);

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
    setSearchQuery('');
  };

  const SelectedIcon = value && ICON_MAP[value] ? ICON_MAP[value] : Check;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || readonly}
            ref={ref}
            className={cn(
              'h-10 w-full justify-between',
              'focus-visible:bg-accent focus-visible:text-accent-foreground',
              'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              !value && 'text-muted-foreground',
            )}
          >
            <div className="flex items-center gap-2">
              {value && ICON_MAP[value] && <SelectedIcon className="h-4 w-4" />}
              <span>{value || t('configEditor.iconSelector.selectIcon')}</span>
            </div>
            <ChevronDown
              className={cn('ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform', {
                'rotate-180': open,
              })}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[10000] max-w-[320px] p-0" align="start">
          {/* Search input */}
          <div className="border-b p-3">
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder={t('configEditor.iconSelector.searchIcons')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-9"
                autoFocus
              />
            </div>
          </div>

          {/* Icon grid */}
          <ScrollArea className="h-[300px]">
            <div className="space-y-4 p-3">
              {Object.entries(groupedIcons).map(([category, icons]) => (
                <div key={category}>
                  <Label className="text-muted-foreground mb-2 block text-xs font-medium">{category}</Label>
                  <div className="grid grid-cols-5 gap-1">
                    {icons.map((iconName) => {
                      const Icon = ICON_MAP[iconName];
                      if (!Icon) return null;

                      const isSelected = value === iconName;

                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => handleSelect(iconName)}
                          className={cn(
                            'flex flex-col items-center justify-center rounded-md p-2 transition-colors',
                            'hover:bg-accent hover:text-accent-foreground',
                            'focus-visible:bg-accent focus-visible:text-accent-foreground',
                            'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                            isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                          )}
                          title={iconName}
                        >
                          <Icon className="h-5 w-5" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {Object.keys(groupedIcons).length === 0 && (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  {t('configEditor.iconSelector.noIconsFound', { query: searchQuery })}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Selected icon preview */}
          {value && (
            <div className="bg-muted/50 border-t p-3">
              <div className="flex items-center gap-2 text-sm">
                <SelectedIcon className="h-4 w-4" />
                <span className="font-medium">{value}</span>
                <span className="text-muted-foreground ml-auto">{t('configEditor.iconSelector.selected')}</span>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default IconSelectorWidget;
